import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import DatePicker from '@adsk/bim360-matrix-react-date-picker';
import DateSelector from '@adsk/bim360-matrix-react-date-selector';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import LocationPicker from '@adsk/bim360-matrix-react-location-picker';
import {WithScrollableContainerRef} from '@adsk/bim360-matrix-react-helpers';
import {
  locationsTreeNodesType,
  oxygenType,
  templateCategoriesEntitiesMapType,
  templateCategoryType,
  templatesQueryParamsType
} from '../../constants/proptype-validation';
import {COLLECTION_TYPES, TEMPLATES_FILTER_PROPERTIES} from '../../constants/issue-query-params';
import FormField from '../common/FormField';
import i18n from '../../utils/i18n';
import {issueTemplatesStatuses, statusColorMapping} from '../../constants/status-types';
import {preloadedFilterQueryParamsState} from '../../constants/preloaded-states';
import {viewTypes} from '../../constants/view-types';
import {findProjectMemberInOxygenMappingById, getUserCompanyName} from '../../utils/userHelpers';
import {toIsoDate} from '../../utils/date-helpers';
import {TEMPLATE_ACTIVE_TYPES} from '../../constants/issue-templates';
import SelectBoxAdapter from '../common/SelectBoxAdapter';

const scrollableClassname = 'StickyScrollableContainer__content';
const DatePickerWithContainer = WithScrollableContainerRef(DatePicker, scrollableClassname);
const LocationPickerWithContainer = WithScrollableContainerRef(LocationPicker, scrollableClassname);

class TemplatesFilter extends PureComponent {
  constructor(props) {
    super(props);

    this.dropdownMaxWidth = 250;
    this.statusOptions = issueTemplatesStatuses.map(status => ({label: i18n.t(`status.${status}`), value: status}));
    this.activeOption = {
      label: i18n.t('filter.activeOption'),
      value: TEMPLATE_ACTIVE_TYPES.ACTIVE
    };
    this.inactiveOption = {
      label: i18n.t('filter.inactiveOption'),
      value: TEMPLATE_ACTIVE_TYPES.INACTIVE
    };
    this.isActiveOptions = [this.activeOption, this.inactiveOption];
  }

  getPreloadedQueryParamsState() {
    return preloadedFilterQueryParamsState[viewTypes.PROJECT][COLLECTION_TYPES.TEMPLATES];
  }

  getPreloadedQueryParam(key) {
    const preloadedFilterState = this.getPreloadedQueryParamsState();
    return preloadedFilterState[key];
  }

  updateFilterQueryParams(filter) {
    this.props.setQueryParamsAction(filter, COLLECTION_TYPES.TEMPLATES);
  }

  handleFilterChange(fieldName, value) {
    this.updateFilterQueryParams({[fieldName]: value});
  }

  handleCategoryChange = categories => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.CATEGORY, categories.map(c => c.id));
  };

  handleSubtypeChange = subtypes => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.SUBTYPE, subtypes.map(s => s.id));
  };

  handleStatusChange = statuses => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.STATUS, statuses.map(s => s.value));
  };

  handleLocationChange = location => {
    this.handleFilterChange(
      TEMPLATES_FILTER_PROPERTIES.LOCATION,
      location ? location.id : this.getPreloadedQueryParam(TEMPLATES_FILTER_PROPERTIES.LOCATION)
    );
  };

  handleAssignedToChange = assignedToArray => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.ASSIGNED_TO, assignedToArray.map(entity => entity.oxygenId));
  };

  handleOwnerChange = owners => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.OWNER, owners.map(user => user.oxygenId));
  };

  handleRootCauseChange = rootCauses => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.ROOT_CAUSE, rootCauses.map(r => r.id));
  };

  handleCreatedByChange = users => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.CREATED_BY, users.map(user => user.oxygenId));
  };

  handleUpdatedByChange = users => {
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.UPDATED_BY, users.map(user => user.oxygenId));
  };

  handleDateSelectorRangeChange(range, startKey, endKey) {
    if (range.startDate === null && range.endDate === null) {
      this.updateFilterQueryParams({
        [startKey]: this.getPreloadedQueryParam(startKey),
        [endKey]: this.getPreloadedQueryParam(endKey)
      });
      return;
    }
    const filter = {};
    if (range.startDate) {
      filter[startKey] = toIsoDate(range.startDate);
    }
    if (range.endDate) {
      filter[endKey] = toIsoDate(range.endDate);
    }
    this.updateFilterQueryParams(filter);
  }

  handleDatePickerRangeChange(range, startKey, endKey) {
    if (range) {
      this.updateFilterQueryParams({
        [startKey]: toIsoDate(range.startDate),
        [endKey]: toIsoDate(range.endDate)
      });
    } else {
      this.updateFilterQueryParams({
        [startKey]: this.getPreloadedQueryParam(startKey),
        [endKey]: this.getPreloadedQueryParam(endKey)
      });
    }
  }

  handleDatePickerCreatedAtChange = range => {
    this.handleDatePickerRangeChange(
      range,
      TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START,
      TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END
    );
  };
  handleDateSelectorCreatedAtChange = range => {
    this.handleDateSelectorRangeChange(
      range,
      TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START,
      TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END
    );
  };

  handleDatePickerUpdatedAtChange = range => {
    this.handleDatePickerRangeChange(
      range,
      TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START,
      TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END
    );
  };
  handleDateSelectorUpdatedAtChange = range => {
    this.handleDateSelectorRangeChange(
      range,
      TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START,
      TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END
    );
  };

  handleIsActiveChange = option => {
    // When no active/inactive are selected - we set the filter to `null` as we don't want to filter by this field.
    // Otherwise - set `true` if active, `false` otherwise
    const isActiveValue = option ? option.value === TEMPLATE_ACTIVE_TYPES.ACTIVE : null;
    this.handleFilterChange(TEMPLATES_FILTER_PROPERTIES.IS_ACTIVE, isActiveValue);
  };

  renderCategoryField() {
    const {templateCategoriesEntitiesMap, queryParams, templateCategories} = this.props;
    return (
      <FormField labelText={i18n.t('filter.templateCategory')}>
        <SelectBoxAdapter
          options={templateCategories}
          selectedOptions={queryParams[TEMPLATES_FILTER_PROPERTIES.CATEGORY].map(
            categoryId => templateCategoriesEntitiesMap[categoryId]
          )}
          multi
          clearable
          searchable
          labelKey="title"
          valueKey="id"
          onChange={this.handleCategoryChange}
          dropdownMaxWidth={this.dropdownMaxWidth}
          placeholder={i18n.t('filter.templateCategoryPlaceholder')}
          searchPlaceholder={i18n.t('filter.templateCategorySearchPlaceholder')}
          searchIcon
        />
      </FormField>
    );
  }

  renderSubTypeField() {
    const {queryParams, subTypesGroupedDropdownOptions, fieldIssueTypeEntitiesMap} = this.props;

    return (
      <FormField labelText={i18n.t('filter.issueType')}>
        <SelectBoxAdapter
          options={subTypesGroupedDropdownOptions}
          selectedOptions={queryParams[TEMPLATES_FILTER_PROPERTIES.SUBTYPE].map(
            subtypeId => fieldIssueTypeEntitiesMap[subtypeId]
          )}
          grouped
          multi
          clearable
          searchable
          labelKey="title"
          valueKey="id"
          onChange={this.handleSubtypeChange}
          dropdownMaxWidth={this.dropdownMaxWidth}
          placeholder={i18n.t('filter.issueTypePlaceholder')}
          searchPlaceholder={i18n.t('filter.issueTypeSearchPlaceholder')}
          searchIcon
        />
      </FormField>
    );
  }

  statusOptionRenderer(value, label) {
    return <StatusColorIndicatorOption statusColor={statusColorMapping[value]} label={label} />;
  }

  renderStatusField() {
    const {queryParams} = this.props;
    const selectedStatuses = this.statusOptions.filter(status =>
      queryParams[TEMPLATES_FILTER_PROPERTIES.STATUS].includes(status.value)
    );
    return (
      <FormField labelText={i18n.t('filter.status')}>
        <SelectBoxAdapter
          options={this.statusOptions}
          selectedOptions={selectedStatuses}
          optionRenderer={this.statusOptionRenderer}
          onChange={this.handleStatusChange}
          placeholder={i18n.t('filter.statusPlaceholder')}
          clearable
          multi
        />
      </FormField>
    );
  }

  renderLocationPicker() {
    const {
      queryParams,
      isLocationsTreeLoading,
      isLocationsLoadingError,
      locationsTreeNodes,
      getDefaultLocationTree
    } = this.props;
    return (
      <FormField labelText={i18n.t('filter.location')}>
        <LocationPickerWithContainer
          placeholder={i18n.t('filter.locationPlaceholder')}
          searchPlaceholder={i18n.t('filter.locationSearchPlaceholder')}
          onChange={this.handleLocationChange}
          locationApiPayload={locationsTreeNodes}
          isLoading={isLocationsTreeLoading}
          loadingError={isLocationsLoadingError}
          onTryAgain={getDefaultLocationTree}
          selectedEventKey={queryParams[TEMPLATES_FILTER_PROPERTIES.LOCATION]}
        />
      </FormField>
    );
  }

  renderAssignedToField() {
    const {queryParams, groupedProjectEntities, oxygen} = this.props;

    const selectedOptions = queryParams[TEMPLATES_FILTER_PROPERTIES.ASSIGNED_TO].map(assigneeId =>
      findProjectMemberInOxygenMappingById(assigneeId, oxygen)
    );

    return (
      <FormField labelText={i18n.t('filter.assignedTo')}>
        <SelectBoxAdapter
          options={groupedProjectEntities}
          selectedOptions={selectedOptions}
          grouped
          multi
          clearable
          searchable
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.assignedToPlaceholder')}
          onChange={this.handleAssignedToChange}
          searchIcon
          searchPlaceholder={i18n.t('filter.assignedToSearch')}
        />
      </FormField>
    );
  }

  renderOwnerField() {
    const {queryParams, users, oxygen} = this.props;

    const selectedOptions = queryParams[TEMPLATES_FILTER_PROPERTIES.OWNER].map(userId => oxygen.users[userId]);

    return (
      <FormField labelText={i18n.t('filter.owner')}>
        <SelectBoxAdapter
          options={users}
          selectedOptions={selectedOptions}
          multi
          clearable
          searchable
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.ownerPlaceholder')}
          onChange={this.handleOwnerChange}
          searchIcon
          searchPlaceholder={i18n.t('filter.ownerSearch')}
        />
      </FormField>
    );
  }

  renderRootCauseField() {
    const {queryParams, issueRootCausesGroupedDropdownOptions, rootCauseEntitiesMap} = this.props;
    const textNoAvailableRootCause = issueRootCausesGroupedDropdownOptions.length
      ? undefined
      : i18n.t('new.noAvailableRootCauses');
    return (
      <FormField labelText={i18n.t('filter.rootCause')}>
        <SelectBoxAdapter
          options={issueRootCausesGroupedDropdownOptions}
          selectedOptions={queryParams[TEMPLATES_FILTER_PROPERTIES.ROOT_CAUSE].map(
            rootCauseId => rootCauseEntitiesMap[rootCauseId]
          )}
          grouped
          multi
          clearable
          searchable
          labelKey="title"
          valueKey="id"
          onChange={this.handleRootCauseChange}
          dropdownMaxWidth={this.dropdownMaxWidth}
          placeholder={i18n.t('filter.rootCausePlaceholder')}
          searchPlaceholder={i18n.t('filter.rootCauseSearchPlaceholder')}
          searchIcon
          noResultsValue={textNoAvailableRootCause}
        />
      </FormField>
    );
  }

  renderCreatedByField() {
    const {queryParams, users, oxygen} = this.props;

    const selectedOptions = queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_BY].map(userId => oxygen.users[userId]);

    return (
      <FormField labelText={i18n.t('filter.createdBy')}>
        <SelectBoxAdapter
          options={users}
          selectedOptions={selectedOptions}
          multi
          clearable
          searchable
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.selectUserPlaceholder')}
          onChange={this.handleCreatedByChange}
          searchIcon
        />
      </FormField>
    );
  }

  renderCreatedAtField() {
    const {queryParams, isDateSelectorMigrationEnabled} = this.props;

    return (
      <FormField labelText={i18n.t('filter.createdAt')}>
        {isDateSelectorMigrationEnabled && (
          <DateSelector
            inputProps={{placeholder: i18n.t('filter.chooseDatePlaceholder')}}
            range
            startDate={queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START]}
            endDate={queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END]}
            onChange={this.handleDateSelectorCreatedAtChange}
            popupContainer={this.props.container}
          />
        )}
        {!isDateSelectorMigrationEnabled && (
          <DatePickerWithContainer
            placeholder={i18n.t('filter.chooseDatePlaceholder')}
            range
            startDate={queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START]}
            endDate={queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END]}
            onChange={this.handleDatePickerCreatedAtChange}
          />
        )}
      </FormField>
    );
  }

  renderUpdatedByField() {
    const {queryParams, users, oxygen} = this.props;

    const selectedOptions = queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_BY].map(userId => oxygen.users[userId]);

    return (
      <FormField labelText={i18n.t('filter.updatedBy')}>
        <SelectBoxAdapter
          options={users}
          selectedOptions={selectedOptions}
          multi
          clearable
          searchable
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.selectUserPlaceholder')}
          onChange={this.handleUpdatedByChange}
          searchIcon
        />
      </FormField>
    );
  }

  renderUpdatedAtField() {
    const {queryParams, isDateSelectorMigrationEnabled} = this.props;

    return (
      <FormField labelText={i18n.t('filter.updatedAt')}>
        {isDateSelectorMigrationEnabled && (
          <DateSelector
            inputProps={{placeholder: i18n.t('filter.chooseDatePlaceholder')}}
            range
            startDate={queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START]}
            endDate={queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END]}
            onChange={this.handleDateSelectorUpdatedAtChange}
            popupContainer={this.props.container}
          />
        )}
        {!isDateSelectorMigrationEnabled && (
          <DatePickerWithContainer
            placeholder={i18n.t('filter.chooseDatePlaceholder')}
            range
            startDate={queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START]}
            endDate={queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END]}
            onChange={this.handleDatePickerUpdatedAtChange}
          />
        )}
      </FormField>
    );
  }

  getIsActiveSelectedOption() {
    const isActiveValue = this.props.queryParams[TEMPLATES_FILTER_PROPERTIES.IS_ACTIVE];
    if (isActiveValue === null) {
      return null;
    }
    return isActiveValue ? this.activeOption : this.inactiveOption;
  }

  renderIsActiveField() {
    const selectedOptions = this.getIsActiveSelectedOption();

    return (
      <FormField labelText={i18n.t('filter.isActive')}>
        <SelectBoxAdapter
          options={this.isActiveOptions}
          selectedOptions={selectedOptions}
          clearable
          onChange={this.handleIsActiveChange}
        />
      </FormField>
    );
  }

  render() {
    return (
      <div className="TemplatesFilter">
        {this.renderCategoryField()}
        {this.renderSubTypeField()}
        {this.renderStatusField()}
        {this.renderLocationPicker()}
        {this.renderAssignedToField()}
        {this.renderOwnerField()}
        {this.renderRootCauseField()}
        {this.renderCreatedByField()}
        {this.renderCreatedAtField()}
        {this.renderUpdatedByField()}
        {this.renderUpdatedAtField()}
        {this.renderIsActiveField()}
      </div>
    );
  }
}

TemplatesFilter.displayName = 'TemplatesFilter';

TemplatesFilter.propTypes = {
  templateCategoriesEntitiesMap: templateCategoriesEntitiesMapType.isRequired,
  templateCategories: PropTypes.arrayOf(templateCategoryType).isRequired,
  queryParams: templatesQueryParamsType.isRequired,
  setQueryParamsAction: PropTypes.func.isRequired,
  subTypesGroupedDropdownOptions: PropTypes.array.isRequired,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  isLocationsTreeLoading: PropTypes.bool.isRequired,
  isLocationsLoadingError: PropTypes.bool,
  locationsTreeNodes: locationsTreeNodesType,
  getDefaultLocationTree: PropTypes.func.isRequired,
  oxygen: oxygenType.isRequired,
  groupedProjectEntities: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  issueRootCausesGroupedDropdownOptions: PropTypes.array.isRequired,
  rootCauseEntitiesMap: PropTypes.object.isRequired,
  isDateSelectorMigrationEnabled: PropTypes.bool,
  container: PropTypes.object
};

export default TemplatesFilter;
